// static/src/stores/contentStore.js
//
// Shared content cache for the two data-driven *marketing* routes: /learn
// (article listing) and /learn/:slug (one article). Both pages used to fetch
// their content in onMounted, which is fine for a pure SPA but fatal for the
// pre-render pipeline: the SSR snapshot (core/prerender.py +
// static/src/prerender-ssr.js) renders the pre-fetch "loading" state, so
// either the snapshot loses all real content (SEO regression) or the client's
// first hydration render (also pre-fetch) disagrees with a content-bearing
// snapshot (hydration mismatch).
//
// Fix: load this data in the router's beforeEach guard instead — it runs and
// is awaited BEFORE the app mounts (main.js awaits router.isReady()), for the
// SSR render and the real client alike. Both sides therefore start their
// first render from the exact same store state, so the snapshot is
// content-bearing AND hydration-exact. The pages seed their local refs from
// the store and simply skip their own initial fetch when seeded.
//
// `fresh` flags mark data that was prefetched for the current mount so the
// page can consume it exactly once; a later in-app navigation re-fetches
// rather than showing stale content.

import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useContentStore = defineStore('content', () => {
  // ── /learn listing ──
  const learn = ref({
    loaded: false,
    articles: [],
    featured: null,
    categories: [],
    totalPages: 1,
    hasMore: false,
    error: false,
  });
  const learnFresh = ref(false);

  async function loadLearn() {
    let featured = null;
    let categories = [];
    let articles = [];
    let totalPages = 1;
    let hasMore = false;
    let error = false;

    try {
      const [catRes, featRes] = await Promise.all([
        fetch('/api/blog/categories'),
        fetch('/api/blog/articles?featured=true&per_page=1'),
      ]);
      if (catRes.ok) {
        categories = (await catRes.json()).categories || [];
      }
      if (featRes.ok) {
        const d = await featRes.json();
        if (d.articles?.length > 0) featured = d.articles[0];
      }

      const res = await fetch('/api/blog/articles?page=1&per_page=12');
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      // Mirror LearnPage's own rule: the featured article is excluded from the
      // default ("All") grid.
      articles = featured
        ? (data.articles || []).filter((a) => a.slug !== featured.slug)
        : (data.articles || []);
      totalPages = data.total_pages || 1;
      hasMore = 1 < totalPages;
    } catch (e) {
      console.error('contentStore.loadLearn failed:', e);
      error = true;
    }

    learn.value = { loaded: true, articles, featured, categories, totalPages, hasMore, error };
    learnFresh.value = true;
    return learn.value;
  }

  function ensureLearn() {
    return learn.value.loaded ? learn.value : loadLearn();
  }

  function consumeLearn() {
    learnFresh.value = false;
  }

  // ── /learn/:slug article ──
  const articlesBySlug = ref({}); // slug -> { loaded, data, fresh }

  async function loadArticle(slug) {
    let data = null;
    try {
      const res = await fetch(`/api/blog/articles/${slug}`);
      if (res.ok) data = (await res.json()).article;
    } catch (e) {
      console.error(`contentStore.loadArticle(${slug}) failed:`, e);
    }
    const record = { loaded: true, data, fresh: true };
    articlesBySlug.value = { ...articlesBySlug.value, [slug]: record };
    return record;
  }

  function ensureArticle(slug) {
    const rec = articlesBySlug.value[slug];
    return rec && rec.loaded ? rec : loadArticle(slug);
  }

  function consumeArticle(slug) {
    const rec = articlesBySlug.value[slug];
    if (rec) articlesBySlug.value = { ...articlesBySlug.value, [slug]: { ...rec, fresh: false } };
  }

  return {
    learn, learnFresh, loadLearn, ensureLearn, consumeLearn,
    articlesBySlug, loadArticle, ensureArticle, consumeArticle,
  };
});
